import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { OrderStatusModel } from '@merchant-clients/order';

import {
  allowedPeriodTypeValues,
  type PeriodModel,
} from '@frontend/modules/order';
import type {
  OrdersGroupByFieldModel,
  OrdersSortDirection,
} from '@frontend/modules/order/hooks';
import {
  AllowedOrdersSortField,
  OrdersSortField,
} from '@frontend/modules/order/order-sort.model';
import {
  getDefaultStatsFilter,
  parseStoredStatsFilter,
  type StatsFilter,
} from '@frontend/modules/stats/components/filter/stats-filter-input.interface';
import { KeyValueStore } from '@frontend/utils';

import { migratePersistedPlaceIds } from './place-ids.utils';

export const AllowedOrderCardSizeVariantTypeValues = [
  'small',
  'medium',
  'large',
] as const;

export type OrderCardSizeVariantType =
  (typeof AllowedOrderCardSizeVariantTypeValues)[number];

export const AllowedAudioTuneTypeValues = [
  'BELL',
  'GOAT',
  'NOTIFICATION',
  'BOAT_HORN',
] as const;

export type AudioTuneType = (typeof AllowedAudioTuneTypeValues)[number];

export interface OrderCardSettingsModel {
  showItems: boolean;
  showGuestName: boolean;
  showTimeParameters: boolean;
  itemFontSize: OrderCardSizeVariantType;
  cardSize: OrderCardSizeVariantType;
}

export interface OrderAudioSettingsModel {
  enabled: boolean;
  tune: AudioTuneType;
}

export interface OrderFilter {
  preOrder: number | undefined;
  status: OrderStatusModel[];
  period: PeriodModel;
  placeIds: string[] | undefined;
}

export interface OrdersPageSettingsModel {
  orderCardSettings: OrderCardSettingsModel;
  audio: OrderAudioSettingsModel;
  groupBy: OrdersGroupByFieldModel;
  sortField: OrdersSortField;
  sortDirection: OrdersSortDirection;
  filter: OrderFilter;
}

export interface StatsPageSettingsModel {
  filter: StatsFilter;
}

const PersistentUiSettingsContext =
  createContext<PersistentUiSettingsContextType>(null!);

export interface PersistentUiSettingsContextType {
  ordersPage: OrdersPageSettingsModel;
  updateOrdersPage: (settings: Partial<OrdersPageSettingsModel>) => void;
  statsPage: StatsPageSettingsModel;
  updateStatsPage: (settings: Partial<StatsPageSettingsModel>) => void;
}

const STATS_PAGE_STORAGE_KEY = 'merchant_ui_settings_stats';

const DEFAULT_ORDERS_PAGE_SETTINGS: OrdersPageSettingsModel = {
  orderCardSettings: {
    showItems: true,
    showGuestName: true,
    showTimeParameters: true,
    itemFontSize: 'small',
    cardSize: 'small',
  },
  audio: {
    enabled: true,
    tune: 'BELL',
  },
  groupBy: 'STATUS',
  sortField: 'VERIFICATION',
  sortDirection: 'ASC',
  filter: {
    period: 'TODAY',
    status: ['CONFIRMED', 'READY', 'CLOSED'],
    placeIds: undefined,
    preOrder: undefined,
  },
};

const getDefaultStatsPageSettings = (): StatsPageSettingsModel => ({
  filter: getDefaultStatsFilter(),
});

export const PersistentUiSettingsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [ordersPageSettings, setOrdersPageSettings] =
    useState<OrdersPageSettingsModel>(DEFAULT_ORDERS_PAGE_SETTINGS);
  const [statsPageSettings, setStatsPageSettings] =
    useState<StatsPageSettingsModel>(getDefaultStatsPageSettings);

  useEffect(() => {
    KeyValueStore.get(STATS_PAGE_STORAGE_KEY)
      .then((stored) => {
        if (!stored) {
          return;
        }

        setStatsPageSettings({ filter: parseStoredStatsFilter(stored) });
      })
      .catch((e) => {
        // Keep the defaults, the stored settings are rewritten on the next change.
        console.error(e);
      });
  }, []);

  useEffect(() => {
    KeyValueStore.get(`merchant_ui_settings_orders`).then((stored) => {
      if (!stored) {
        KeyValueStore.set(
          `merchant_ui_settings_orders`,
          JSON.stringify(DEFAULT_ORDERS_PAGE_SETTINGS),
        );
        return;
      }

      const cached = JSON.parse(stored);
      setOrdersPageSettings({
        ...cached,
        orderCardSettings: {
          ...DEFAULT_ORDERS_PAGE_SETTINGS.orderCardSettings,
          ...cached.orderCardSettings,
        },
        audio: {
          ...DEFAULT_ORDERS_PAGE_SETTINGS.audio,
          ...cached.audio,
        },
        groupBy: cached.groupBy ?? DEFAULT_ORDERS_PAGE_SETTINGS.groupBy,
        sortField: (AllowedOrdersSortField as readonly string[]).includes(
          cached.sortField,
        )
          ? (cached.sortField as OrdersSortField)
          : cached.sortField == null
            ? cached.groupBy === 'NONE'
              ? 'VERIFICATION'
              : 'TIME'
            : DEFAULT_ORDERS_PAGE_SETTINGS.sortField,
        sortDirection:
          cached.sortDirection ?? DEFAULT_ORDERS_PAGE_SETTINGS.sortDirection,
        filter: {
          period:
            cached.filter?.period &&
            (allowedPeriodTypeValues as readonly string[]).includes(
              cached.filter.period,
            )
              ? (cached.filter.period as PeriodModel)
              : DEFAULT_ORDERS_PAGE_SETTINGS.filter.period,
          status:
            cached.filter?.status ?? DEFAULT_ORDERS_PAGE_SETTINGS.filter.status,
          placeIds: migratePersistedPlaceIds(
            cached.filter,
            DEFAULT_ORDERS_PAGE_SETTINGS.filter.placeIds,
          ),
          preOrder:
            cached.filter?.preOrder ??
            DEFAULT_ORDERS_PAGE_SETTINGS.filter.preOrder,
        },
      });
    });
  }, []);

  const updateOrdersPage = (settings: Partial<OrdersPageSettingsModel>) => {
    setOrdersPageSettings((prev) => {
      const updated = { ...prev, ...settings };
      KeyValueStore.set(`merchant_ui_settings_orders`, JSON.stringify(updated));
      return updated;
    });
  };

  const updateStatsPage = (settings: Partial<StatsPageSettingsModel>) => {
    setStatsPageSettings((prev) => {
      const updated = { ...prev, ...settings };
      KeyValueStore.set(STATS_PAGE_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <PersistentUiSettingsContext.Provider
      value={{
        ordersPage: ordersPageSettings,
        updateOrdersPage,
        statsPage: statsPageSettings,
        updateStatsPage,
      }}
    >
      {children}
    </PersistentUiSettingsContext.Provider>
  );
};

export const usePersistentUiSettings = () => {
  return useContext(PersistentUiSettingsContext);
};
